
import { create } from 'zustand';
import { ClientLogo } from '../types';
import { supabase } from '@/integrations/supabase/client';

export const useClientLogoStore = create<{
  logos: ClientLogo[];
  loading: boolean;
  error: string | null;
  fetchLogos: () => Promise<void>;
  addLogo: (logoData: Partial<ClientLogo>) => Promise<void>;
  updateLogo: (id: string, updates: Partial<ClientLogo>) => Promise<void>;
  removeLogo: (id: string) => Promise<void>;
}>((set, get) => ({
  logos: [],
  loading: false,
  error: null,
  
  fetchLogos: async () => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('client_logos')
        .select('*')
        .order('name') as any;
        
      if (error) throw error;
      
      set({ 
        logos: (data || []).map((logo: any) => ({
          id: logo.id,
          name: logo.name,
          imageUrl: logo.image_url,
          altText: logo.alt_text || undefined,
          url: logo.url || undefined,
          createdAt: new Date(logo.created_at).getTime()
        })),
        loading: false 
      });
    } catch (err: any) {
      console.error('Error fetching logos:', err);
      set({ error: err.message, loading: false });
    }
  },
  
  addLogo: async (logoData) => {
    const { logos } = get();
    if (logos.length >= 20) {
      throw new Error('Maximum of 20 logos allowed');
    }
    
    set({ loading: true, error: null });
    try {
      // Check if user is authenticated and has admin privileges
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Authentication required to add logos');
      }
      
      if (!user.email?.endsWith('@redflags.au')) {
        throw new Error('Admin privileges required to add logos');
      }
      
      const { data, error } = await supabase
        .from('client_logos')
        .insert({
          name: logoData.name,
          image_url: logoData.imageUrl,
          alt_text: logoData.altText,
          url: logoData.url
        })
        .select() as any;
        
      if (error) throw error;
      
      if (data && data.length > 0) {
        const newLogo = data[0];
        set(state => ({ 
          logos: [
            ...state.logos, 
            {
              id: newLogo.id,
              name: newLogo.name,
              imageUrl: newLogo.image_url,
              altText: newLogo.alt_text || undefined,
              url: newLogo.url || undefined,
              createdAt: new Date(newLogo.created_at).getTime()
            }
          ],
          loading: false 
        }));
      } else {
        throw new Error('No data returned from insert operation');
      }
    } catch (err: any) {
      console.error('Error adding logo:', err);
      set({ error: err.message, loading: false });
      throw err;
    }
  },
  
  updateLogo: async (id, updates) => {
    set({ loading: true, error: null });
    try {
      // Check if user is authenticated and has admin privileges
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Authentication required to update logos');
      }
      
      if (!user.email?.endsWith('@redflags.au')) {
        throw new Error('Admin privileges required to update logos');
      }
      
      const { error } = await supabase
        .from('client_logos')
        .update({
          name: updates.name,
          image_url: updates.imageUrl,
          alt_text: updates.altText,
          url: updates.url
        })
        .eq('id', id) as any;
        
      if (error) throw error;
      
      set(state => ({
        logos: state.logos.map(logo => 
          logo.id === id ? { ...logo, ...updates } : logo
        ),
        loading: false
      }));
    } catch (err: any) {
      console.error('Error updating logo:', err);
      set({ error: err.message, loading: false });
      throw err;
    }
  },
  
  removeLogo: async (id) => {
    set({ loading: true, error: null });
    try {
      // Check if user is authenticated and has admin privileges
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Authentication required to remove logos');
      }
      
      if (!user.email?.endsWith('@redflags.au')) {
        throw new Error('Admin privileges required to remove logos');
      }
      
      const { error } = await supabase
        .from('client_logos')
        .delete()
        .eq('id', id) as any;
        
      if (error) throw error;
      
      set(state => ({
        logos: state.logos.filter(logo => logo.id !== id),
        loading: false
      }));
    } catch (err: any) {
      console.error('Error removing logo:', err);
      set({ error: err.message, loading: false });
      throw err;
    }
  }
}));
